import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Area
} from 'recharts';
import { FaEye, FaTimes, FaSearch, FaFilter, FaDownload, FaPlus, FaExternalLinkAlt } from 'react-icons/fa';
import { 
  ShoppingCart, CalendarCheck, BarChart2, CheckCircle, XCircle, Eye, X, TrendingUp, Users, DollarSign, Package,
  // Added new icons for the requested stats
  Building2, ClipboardList, FileText, ArrowUpCircle, ArrowDownCircle
} from 'lucide-react';

import { useScreenPermissions } from '../components/useScreenPermissions'; 
// Add these imports alongside your existing imports
import SurveyBOQViewModal from './SurveyBOQViewModal'; // Adjust path
import SiteIndentViewModal from './SiteIndentViewModal'; // Adjust path
// Add these imports
import MaterialIssueViewModal from './MaterialIssueViewModal';
import MaterialReceiveViewModal from './MaterialReceiveViewModal';
import MaterialReceiptViewModal from './MaterialReceiptViewModal';

const API_URL = '/api';

// Updated chart data with new color scheme
const barData = [
  { name: 'Jan', revenue: 42000, orders: 240 },
  { name: 'Feb', revenue: 38000, orders: 139 },
  { name: 'Mar', revenue: 52000, orders: 980 },
  { name: 'Apr', revenue: 45000, orders: 390 },
  { name: 'May', revenue: 61000, orders: 480 },
  { name: 'Jun', revenue: 55000, orders: 380 },
];

const lineData = [
  { name: 'Mon', value: 120, target: 100 },
  { name: 'Tue', value: 210, target: 150 },
  { name: 'Wed', value: 180, target: 200 },
  { name: 'Thu', value: 160, target: 180 },
  { name: 'Fri', value: 250, target: 220 },
  { name: 'Sat', value: 190, target: 160 },
  { name: 'Sun', value: 140, target: 120 },
];

const pieData = [
  { name: 'Electronics', value: 35, color: '#033A3E' },
  { name: 'Clothing', value: 25, color: '#075D5F' },
  { name: 'Home & Garden', value: 20, color: '#f1fbfb' },
  { name: 'Sports', value: 15, color: '#0f1724' },
  { name: 'Others', value: 5, color: '#6b7280' },
];

// Sample PR Data
const prData = [
  { prNo: 'PR-001', prDate: '2024-01-15', prStatus: 'Approved', submissionDate: '2024-01-10', amount: '$12,500', category: 'Electronics' },
  { prNo: 'PR-002', prDate: '2024-01-20', prStatus: 'Pending', submissionDate: '2024-01-18', amount: '$8,750', category: 'Office Supplies' },
  { prNo: 'PR-003', prDate: '2024-01-25', prStatus: 'Rejected', submissionDate: '2024-01-22', amount: '$15,200', category: 'Furniture' },
  { prNo: 'PR-004', prDate: '2024-01-30', prStatus: 'Approved', submissionDate: '2024-01-28', amount: '$9,800', category: 'IT Equipment' },
  { prNo: 'PR-005', prDate: '2024-02-05', prStatus: 'Pending', submissionDate: '2024-02-01', amount: '$22,100', category: 'Machinery' },
];

// Sample PO Data
const poData = [
  { pon: 'PO-001', poDate: '2024-01-20', currentStatus: 'Active', shippedStatus: 'Shipped', receptStatus: 'Received', overdueStatus: 'On Time', due: '2024-02-15', amount: '$12,500' },
  { pon: 'PO-002', poDate: '2024-01-25', currentStatus: 'Active', shippedStatus: 'In Transit', receptStatus: 'Pending', overdueStatus: 'On Time', due: '2024-02-20', amount: '$8,750' },
  { pon: 'PO-003', poDate: '2024-01-30', currentStatus: 'Completed', shippedStatus: 'Delivered', receptStatus: 'Received', overdueStatus: 'Overdue', due: '2024-02-10', amount: '$15,200' },
  { pon: 'PO-004', poDate: '2024-02-05', currentStatus: 'Active', shippedStatus: 'Pending', receptStatus: 'Pending', overdueStatus: 'On Time', due: '2024-03-01', amount: '$9,800' },
  { pon: 'PO-005', poDate: '2024-02-10', currentStatus: 'Cancelled', shippedStatus: 'Cancelled', receptStatus: 'Cancelled', overdueStatus: 'Cancelled', due: '2024-03-05', amount: '$22,100' },
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [assignedDashboards, setAssignedDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPRModal, setShowPRModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [prSearchTerm, setPrSearchTerm] = useState('');
  const [poSearchTerm, setPoSearchTerm] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess } = useScreenPermissions();
  const { hasScreenAccess } = useScreenPermissions(); // Add this line to get route permission checker
  const [surveyList, setSurveyList] = useState([]);
  const [indentList, setIndentList] = useState([]);
    // Add these 4 new state variables
  const [showSurveyViewModal, setShowSurveyViewModal] = useState(false);
  const [showIndentViewModal, setShowIndentViewModal] = useState(false);
  const [selectedSurveyForView, setSelectedSurveyForView] = useState(null);
  const [selectedIndentForView, setSelectedIndentForView] = useState(null);

    // Add these new state variables for Lists
  const [mrnList, setMRNList] = useState([]);
  const [issueList, setIssueList] = useState([]);
  const [receiveList, setReceiveList] = useState([]);

  // Add these new state variables for Modals
  const [showIssueViewModal, setShowIssueViewModal] = useState(false);
  const [showReceiveViewModal, setShowReceiveViewModal] = useState(false);
  const [selectedIssueForView, setSelectedIssueForView] = useState(null);
  const [selectedReceiveForView, setSelectedReceiveForView] = useState(null);
  const [showMRNViewModal, setShowMRNViewModal] = useState(false);
  const [selectedMRNForView, setSelectedMRNForView] = useState(null);

  // NEW: State for inventory stats
   // Update this state object:
  const [inventoryStats, setInventoryStats] = useState({
    totalProjects: 0,
    totalSurvey: 0,
    totalIndent: 0,
    totalIssued: 0,
    totalReceived: 0,
    // Add these new lines:
    totalProjectQty: 0,
    totalSurveyQty: 0,
    totalIndentQty: 0,
    totalIssueQty: 0,
    totalReceiveQty: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          navigate('/login');
          return;
        }
        const response = await fetch(`${API_URL}/current-user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok && data.user) {
          // Ensure IsActive is set to true by default if not provided
          setCurrentUser({
            ...data.user,
            IsActive: data.user.IsActive !== undefined ? data.user.IsActive : true
          });
          
          // Fetch assigned dashboards for this user
          fetchAssignedDashboards(data.user.UserID);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentUser();
  }, [navigate]);

  // NEW: Fetch Inventory Stats
  useEffect(() => {
    const fetchInventoryStats = async () => {
      try {
        // You need to ensure your backend exposes this endpoint with the data calculated from SQL
        const response = await fetch(`${API_URL}/dashboard-stats` , {headers: {'authorization': `Bearer ${localStorage.getItem('authToken')}`}});
        if (response.ok) {
          const data = await response.json();
          setInventoryStats(data);
        } else {
          console.error('Failed to fetch inventory stats');
        }
      } catch (error) {
        console.error('Error fetching inventory stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    
    // Only fetch if we are logged in (simulated here by checking if token exists or component mounted)
    const token = localStorage.getItem('authToken');
    if(token) fetchInventoryStats();
  }, []);

  // Function to fetch assigned dashboards
  const fetchAssignedDashboards = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/users/${userId}/dashboards`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssignedDashboards(data);
      } else {
        console.error('Failed to fetch assigned dashboards');
      }
    } catch (error) {
      console.error('Error fetching assigned dashboards:', error);
    }
  };

  // Add this useEffect to fetch lists
   // Update the fetchDashboardLists useEffect
  useEffect(() => {
    const fetchDashboardLists = async () => {
      try {
        const token = localStorage.getItem('authToken');

         const surveyRes = await fetch(`${API_URL}/dashboard/survey-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if(surveyRes.ok) setSurveyList(await surveyRes.json());

        // Fetch Indent List
        const indentRes = await fetch(`${API_URL}/dashboard/indent-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if(indentRes.ok) setIndentList(await indentRes.json());
        
        // Fetch MRN List
        const mrnRes = await fetch(`${API_URL}/dashboard/mrn-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if(mrnRes.ok) setMRNList(await mrnRes.json());

        // Fetch Issue List
        const issueRes = await fetch(`${API_URL}/dashboard/issue-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if(issueRes.ok) setIssueList(await issueRes.json());

        // Fetch Receive List
        const receiveRes = await fetch(`${API_URL}/dashboard/receive-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if(receiveRes.ok) setReceiveList(await receiveRes.json());

      } catch (error) {
        console.error('Error fetching dashboard lists:', error);
      }
    };
    fetchDashboardLists();
  }, []);

   const handleViewSurvey = async (item) => {
    // 1. Check Permission
    if (!hasFullAccess && !hasPermission('SURVEY_BOQ', 'CanView')) {
      alert("You do not have permission to view Survey BOQ details.");
      return;
    }

    try {
      // 2. Fetch full details (Lines + Header)
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/survey-boq/${item.SurveyBOQID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch details');
      
      const result = await response.json();
      
      // 3. Normalize data structure for the Modal (flatten header & lines)
      const normalizedData = {
        ...result.header,
        Lines: result.lines
      };

      // 4. Open Modal
      setSelectedSurveyForView(normalizedData);
      setShowSurveyViewModal(true);
      
    } catch (error) {
      console.error('Error fetching view details:', error);
      alert('Could not load record details');
    }
  };

  const handleViewMoreSurvey = () => {
    // 1. Check Permission to access the List Screen
    if (!hasFullAccess && !hasScreenAccess('SURVEY_BOQ', 'ScreenAccess')) {
      alert("You do not have access to the Survey BOQ screen.");
      return;
    }
    // 2. Navigate
    navigate('/survey-boq');
  };

  const handleViewIndent = async (item) => {
    // 1. Check Permission
    if (!hasFullAccess && !hasPermission('SITE_INDENT', 'CanView')) {
      alert("You do not have permission to view Site Indent details.");
      return;
    }

    try {
      // 2. Fetch full details
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/site-indent/${item.SiteIndentID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch details');
      
      const fullData = await response.json(); 

      // 3. Open Modal
      setSelectedIndentForView(fullData); // The SiteIndentViewModal handles {header, lines} internally
      setShowIndentViewModal(true);
      
    } catch (error) {
      console.error('Error fetching view details:', error);
      alert('Could not load record details');
    }
  };

  const handleViewMoreIndent = () => {
    // 1. Check Permission to access the List Screen
    if (!hasFullAccess && !hasScreenAccess('SITE_INDENT', 'ScreenAccess')) {
      alert("You do not have access to the Site Indent screen.");
      return;
    }
    // 2. Navigate
    navigate('/site-indent');
  };

    // --- MRN HANDLERS ---
   const handleViewMRN = (item) => {
    if (!hasFullAccess && !hasPermission('MRN_MASTER', 'CanView')) {
      alert("You do not have permission to view MRN details.");
      return;
    }
    
    // Pass ONLY the ID, let the modal handle fetching the data
    setSelectedMRNForView(item.MRNDocumentId);
    setShowMRNViewModal(true);
  };

  const handleViewMoreMRN = () => {
    if (!hasFullAccess && !hasScreenAccess('MRN_MASTER')) {
      alert("You do not have access to MRN screen.");
      return;
    }
    navigate('/Material-Receipt-Note');
  };

  // --- ISSUE HANDLERS ---
  const handleViewIssue = async (item) => {
    if (!hasFullAccess && !hasPermission('MATERIAL_ISSUE', 'CanView')) {
      alert("You do not have permission to view Material Issue details.");
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-issue/${item.IssueHeaderID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const fullData = await response.json();
      setSelectedIssueForView(fullData);
      setShowIssueViewModal(true);
    } catch (error) {
      console.error('Error fetching issue details:', error);
      alert('Could not load record details');
    }
  };

  const handleViewMoreIssue = () => {
    if (!hasFullAccess && !hasScreenAccess('MATERIAL_ISSUE')) {
      alert("You do not have access to Material Issue screen.");
      return;
    }
    navigate('/material-issue');
  };

  // --- RECEIVE HANDLERS ---
  const handleViewReceive = async (item) => {
    if (!hasFullAccess && !hasPermission('MATERIAL_RECEIVE', 'CanView')) {
      alert("You do not have permission to view Material Receive details.");
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-receive/${item.ReceiveHeaderID}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const fullData = await response.json();
      setSelectedReceiveForView(fullData);
      setShowReceiveViewModal(true);
    } catch (error) {
      console.error('Error fetching receive details:', error);
      alert('Could not load record details');
    }
  };

  const handleViewMoreReceive = () => {
    if (!hasFullAccess && !hasScreenAccess('MATERIAL_RECEIVE')) {
      alert("You do not have access to Material Receive screen.");
      return;
    }
    navigate('/material-receive');
  };

  // Function to open dashboard in new tab
  const openDashboard = (dashboardCode) => {
    window.open(`/dashboard-view/${dashboardCode}`, '_blank');
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handlePRClick = (pr) => {
    setSelectedPR(pr);
    setShowPRModal(true);
  };

  const handlePOClick = (po) => {
    setSelectedPO(po);
    setShowPOModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
      case 'Active':
      case 'Shipped':
      case 'Delivered':
      case 'Received':
      case 'On Time':
        return 'bg-teal-300 text-white';
      case 'Pending':
      case 'In Transit':
        return 'bg-amber-300 text-white';
      case 'Rejected':
      case 'Cancelled':
      case 'Overdue':
        return 'bg-red-300 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex bg-gray-100">
        <Sidebar onLogout={handleLogout} />
        <main className="flex-1 p-8 overflow-y-auto">
          <div className="flex items-center justify-center h-full">
            <div className="text-primary-500 font-semibold animate-pulse">Loading...</div>
          </div>
        </main>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  // Helper for greeting name
  const getGreetingName = (user) => {
    return user?.FullName || user?.Username || user?.Name || user?.name || user?.email || 'User';
  };

  // NEW: Stats configuration mapped to fetched data
    // Update this stats array:
  const stats = [
    {
      label: 'Total Projects',
      value: statsLoading ? '...' : inventoryStats.totalProjects,
      qty: statsLoading ? '...' : inventoryStats.totalProjectQty, // ADD THIS LINE
      icon: Building2,
      gradient: 'from-blue-500 to-indigo-600',
      iconBg: 'bg-blue-100 text-blue-600'
    },
    {
      label: 'Total Survey',
      value: statsLoading ? '...' : inventoryStats.totalSurvey,
      qty: statsLoading ? '...' : inventoryStats.totalSurveyQty, // ADD THIS LINE
      icon: ClipboardList,
      gradient: 'from-teal-400 to-teal-600',
      iconBg: 'bg-teal-100 text-teal-600'
    },
    {
      label: 'Total Indent',
      value: statsLoading ? '...' : inventoryStats.totalIndent,
      qty: statsLoading ? '...' : inventoryStats.totalIndentQty, // ADD THIS LINE
      icon: FileText,
      gradient: 'from-purple-500 to-pink-600',
      iconBg: 'bg-purple-100 text-purple-600'
    },
    {
      label: 'Issued',
      value: statsLoading ? '...' : inventoryStats.totalIssued,
      qty: statsLoading ? '...' : inventoryStats.totalIssueQty, // ADD THIS LINE
      icon: ArrowUpCircle,
      gradient: 'from-orange-400 to-red-500',
      iconBg: 'bg-orange-100 text-orange-600'
    },
    {
      label: 'Received',
      value: statsLoading ? '...' : inventoryStats.totalReceived,
      qty: statsLoading ? '...' : inventoryStats.totalReceiveQty, // ADD THIS LINE
      icon: ArrowDownCircle,
      gradient: 'from-green-400 to-emerald-600',
      iconBg: 'bg-green-100 text-green-600'
    },
  ];

  // Filter PR data based on the search term
  const filteredPrData = prData.filter(pr =>
    Object.values(pr).some(value =>
      value.toString().toLowerCase().includes(prSearchTerm.toLowerCase())
    )
  );

  // Filter PO data based on the search term
  const filteredPoData = poData.filter(po =>
    Object.values(po).some(value =>
      value.toString().toLowerCase().includes(poSearchTerm.toLowerCase())
    )
  );

  // Theme colors matching the modals
  const colors = {
    primary: '#033A3E',      // Main text color (now dark teal)
    secondary: '#6b7280',    // Secondary text, labels
    muted: '#6b7280',        // Icons and subtle elements
    accent: '#033A3E',       // Active states and highlights (dark teal)
    border: '#d1d5db',       // Card borders
    tabBorder: '#f1fbfb',    // Tab borders (lighter)
    background: '#ffffff',   // Card backgrounds
    pageBackground: '#f3f4f6', // Main background (kept light)
  };

  // Ensure user active status is correctly determined
  const isUserActive = currentUser.IsActive === true || currentUser.IsActive === 'true' || currentUser.IsActive === 1 || currentUser.IsActive === '1';
  
  return (
    <div className="min-h-screen flex bg-gray-100">
      <Sidebar onLogout={handleLogout} 
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
      />
      <main className={`flex-1 p-8 overflow-hidden transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        
        {/* User Welcome Card */}
        <div className="relative animate-fade-in-up w-full max-w-full mb-8">
          <div className="modern-card p-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              {/* Avatar and Greeting Section */}
              <div className="flex items-center gap-6">
                <div className="relative w-20 h-20 rounded-2xl bg-teal-800 shadow-modern flex items-center justify-center ring-4 ring-white/50 hover:ring-primary-200 transition-all duration-300">
                  <span className="text-3xl text-white font-bold">
                    {getGreetingName(currentUser)[0]?.toUpperCase()}
                  </span>
                </div>
                {/* Greeting Text and Status Badges */}
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-dark-500">
                    Welcome back, <span className="text-primary-600">{getGreetingName(currentUser)}</span>! 
                  </h1>
                  <div className="flex items-center gap-3 mt-3">
                    <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold bg-primary-50 text-primary-600 shadow-sm">
                      {currentUser.Role || 'User'}
                    </span>
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold shadow-sm ${
                      isUserActive
                        ? 'text-green-700 bg-green-50'
                        : 'text-red-700 bg-red-50'
                    }`}>
                      {isUserActive ? <CheckCircle size={16} /> : <XCircle size={16} />}
                      {isUserActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              {/* Quick Actions */}
              
            </div>
          </div>
        </div>

        {/* Assigned Dashboards Section
        {assignedDashboards.length > 0 && (
          <div className="modern-card p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-dark-500">Your Assigned Dashboards</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {assignedDashboards.map(dashboard => (
                <button
                  key={dashboard.DashboardID}
                  onClick={() => openDashboard(dashboard.DashboardCode)}
                  className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <span>{dashboard.DashboardName}</span>
                  <FaExternalLinkAlt className="ml-2 text-sm" />
                </button>
              ))}
            </div>
          </div>
        )} */}

        {/* Stats Summary */}
                {/* Stats Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="modern-card p-6 group hover:scale-105 transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                  
                  {/* UPDATE THIS DIV TO SHOW BOTH VALUE AND QTY */}
                  <div className="flex flex-col">
                    <p className="text-2xl font-bold text-dark-500">{stat.value}</p>
                    <p className="text-xs text-gray-400 font-medium mt-1">Total Qty: {stat.qty}</p>
                  </div>
                  
                </div>
                <div className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl ${stat.iconBg} group-hover:scale-110 transition-all duration-300`}>
                  <stat.icon size={24} strokeWidth={2} />
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* PR List Section */}
                {/* Survey List Section (Replaces PR List) */}

                        {/* MRN List Section */}
        {hasScreenAccess('MRN_MASTER') && (
        <div className="w-full bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-dark-500">Recent MRN</h2>
              <button 
                onClick={handleViewMoreMRN}
                className="text-sm font-semibold text-primary-600 hover:text-primary-800 underline"
              >
                View More
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-600 rounded-lg overflow-hidden">
                <thead className="bg-[#eff6ff] text-gray-700 uppercase">
                  <tr>
                    <th className="px-6 py-3 font-semibold text-left">MRN No</th>
                    <th className="px-6 py-3 font-semibold text-left">Invoice Received Date</th>
                    <th className="px-6 py-3 font-semibold text-left">Vendor Code</th>
                    <th className="px-6 py-3 font-semibold text-left">Vendor Invoice No</th>
                    <th className="px-6 py-3 font-semibold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mrnList.length > 0 ? (
                    mrnList.map((item) => (
                      <tr key={item.MRNDocumentId || item.MRNDocumentId} className="bg-white border-b last:border-b-0 border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 font-mono text-blue-600 font-semibold whitespace-nowrap">
                          {item.MRNNumber }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(item.Date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.VendorCode }</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.VendorInvoiceNo}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewMRN(item)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-4 text-gray-400">No recent MRNs found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

                
               {/* Survey List Section */}
         {hasScreenAccess('SURVEY_BOQ') && (        
        <div className="w-full bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 mb-8">
          <div className="p-6">
            {/* --- UPDATE HEADER --- */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-dark-500">Recent Survey BOQ</h2>
              
              {/* View More Button */}
              <button 
                onClick={handleViewMoreSurvey}
                className="text-sm font-semibold text-primary-600 hover:text-primary-800 underline"
              >
                View More
              </button>
            </div>
            {/* ------------------ */}
            
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-600 rounded-lg overflow-hidden">
                {/* Table Head remains the same */}
                <thead className="bg-[#eff6ff] text-gray-700 uppercase">
                  {/* ... existing columns ... */}
                  <tr>
                    <th className="px-6 py-3 font-semibold text-left">Survey No</th>
                    <th className="px-6 py-3 font-semibold text-left">Date</th>
                    <th className="px-6 py-3 font-semibold text-left">Project Code</th>
                    <th className="px-6 py-3 font-semibold text-left">Feeder</th>
                    <th className="px-6 py-3 font-semibold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {surveyList.length > 0 ? (
                    surveyList.map((item) => (
                      <tr key={item.SurveyBOQID} className="bg-white border-b last:border-b-0 border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                        {/* ... existing columns ... */}
                        <td className="px-6 py-4 font-mono text-blue-600 font-semibold whitespace-nowrap">
                          {item.No}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(item.Date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.Category}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.FeederCode}
                        </td>
                        
                        {/* --- UPDATE ACTION BUTTON --- */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewSurvey(item)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                        {/* ----------------------------- */}
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-4 text-gray-400">No recent surveys found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
         )}

                {/* Site Indent Section */}
         {hasScreenAccess('SITE_INDENT') && (        
        <div className="w-full bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 mb-8">
          <div className="p-6">
             {/* --- UPDATE HEADER --- */}
             <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-dark-500">Recent Site Indent</h2>
              
              {/* View More Button */}
              <button 
                onClick={handleViewMoreIndent}
                className="text-sm font-semibold text-primary-600 hover:text-primary-800 underline"
              >
                View More
              </button>
            </div>
            {/* ------------------ */}

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-600 rounded-lg overflow-hidden">
                <thead className="bg-[#eff6ff] text-gray-700 uppercase">
                  <tr>
                    <th className="px-6 py-3 font-semibold text-left">Indent No</th>
                    <th className="px-6 py-3 font-semibold text-left">Date</th>
                    <th className="px-6 py-3 font-semibold text-left">Project No</th>
                    <th className="px-6 py-3 font-semibold text-left">From Feeder</th>
                    <th className="px-6 py-3 font-semibold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {indentList.length > 0 ? (
                    indentList.map((item) => (
                      <tr key={item.SiteIndentID} className="bg-white border-b last:border-b-0 border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 font-mono text-blue-600 font-semibold whitespace-nowrap">
                          {item.No}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(item.Date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.Category}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.FromFeederCode}
                        </td>
                        
                        {/* --- UPDATE ACTION BUTTON --- */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewIndent(item)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                        {/* ----------------------------- */}
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-4 text-gray-400">No recent indents found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
         )}

                 {/* Material Issue Section */}
        {hasScreenAccess('MATERIAL_ISSUE') && (
        <div className="w-full bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-dark-500">Recent Material Issue</h2>
              <button 
                onClick={handleViewMoreIssue}
                className="text-sm font-semibold text-primary-600 hover:text-primary-800 underline"
              >
                View More
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-600 rounded-lg overflow-hidden">
                <thead className="bg-[#eff6ff] text-gray-700 uppercase">
                  <tr>
                    <th className="px-6 py-3 font-semibold text-left">Issue No</th>
                    <th className="px-6 py-3 font-semibold text-left">Date</th>
                    <th className="px-6 py-3 font-semibold text-left">Project</th>
                    <th className="px-6 py-3 font-semibold text-left">Contractor</th>
                    <th className="px-6 py-3 font-semibold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {issueList.length > 0 ? (
                    issueList.map((item) => (
                      <tr key={item.IssueHeaderID} className="bg-white border-b last:border-b-0 border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 font-mono text-blue-600 font-semibold whitespace-nowrap">
                          {item.No}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(item.Date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.ProjectNo || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.VendorCode || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewIssue(item)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-4 text-gray-400">No recent issues found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

                {/* Material Receive Section */}
        {hasScreenAccess('MATERIAL_RECEIVE') && (
        <div className="w-full bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-dark-500">Recent Material Receive</h2>
              <button 
                onClick={handleViewMoreReceive}
                className="text-sm font-semibold text-primary-600 hover:text-primary-800 underline"
              >
                View More
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-600 rounded-lg overflow-hidden">
                <thead className="bg-[#eff6ff] text-gray-700 uppercase">
                  <tr>
                    <th className="px-6 py-3 font-semibold text-left">Receive No</th>
                    <th className="px-6 py-3 font-semibold text-left">Date</th>
                    <th className="px-6 py-3 font-semibold text-left">Project</th>
                    <th className="px-6 py-3 font-semibold text-left">Contractor</th>
                    <th className="px-6 py-3 font-semibold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receiveList.length > 0 ? (
                    receiveList.map((item) => (
                      <tr key={item.ReceiveHeaderID} className="bg-white border-b last:border-b-0 border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 font-mono text-blue-600 font-semibold whitespace-nowrap">
                          {item.No}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(item.Date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{item.ProjectNo || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.VendorCode}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewReceive(item)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-4 text-gray-400">No recent receives found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

       
      </main>

              {/* ... Existing PO/PR Modals ... */}

        {/* NEW: Survey View Modal */}
        {showSurveyViewModal && selectedSurveyForView && (
          <SurveyBOQViewModal
            open={showSurveyViewModal}
            onClose={() => setShowSurveyViewModal(false)}
            data={selectedSurveyForView}
          />
        )}

        {/* NEW: Site Indent View Modal */}
        {showIndentViewModal && selectedIndentForView && (
          <SiteIndentViewModal
            open={showIndentViewModal}
            onClose={() => setShowIndentViewModal(false)}
            data={selectedIndentForView}
          />
        )}

               {/* ... Existing Survey/Indent Modals ... */}

        {/* NEW: Material Issue View Modal */}
        {showIssueViewModal && selectedIssueForView && (
          <MaterialIssueViewModal
            open={showIssueViewModal}
            onClose={() => setShowIssueViewModal(false)}
            data={selectedIssueForView}
          />
        )}

        {/* NEW: Material Receive View Modal */}
        {showReceiveViewModal && selectedReceiveForView && (
          <MaterialReceiveViewModal
            open={showReceiveViewModal}
            onClose={() => setShowReceiveViewModal(false)}
            data={selectedReceiveForView}
          />
        )}

        {showMRNViewModal && selectedMRNForView && (
          <MaterialReceiptViewModal
            open={showMRNViewModal}
            onClose={() => setShowMRNViewModal(false)}
            mrnId={selectedMRNForView}
          />
        )}

    </div>
  );
};

export default Dashboard;